import React, { useEffect, useState } from 'react';
import * as simpleIcons from 'simple-icons';
import { Loader2 } from 'lucide-react';
import { apiGetBrands } from '../lib/api';
import { useSettings } from '../contexts/SettingsContext';

const customLogos = {
  Microsoft: `<svg viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg"><path fill="#F25022" d="M1 1h10v10H1z"/><path fill="#7FBA00" d="M12 1h10v10H12z"/><path fill="#00A4EF" d="M1 12h10v10H1z"/><path fill="#FFB900" d="M12 12h10v10H12z"/></svg>`,
  Adobe: `<svg viewBox="0 0 240 234" xmlns="http://www.w3.org/2000/svg"><path fill="#FA0F00" d="M0 0h152v234L0 0zm240 0v234L88 0h152zM120 86l48 114h-32l-14-36H88l32-78z"/></svg>`,
  OpenAI: `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="#000000" d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/></svg>`,
};

const BrandLogo = ({ brand }) => {
  if (brand.custom_logo && customLogos[brand.custom_logo]) {
    return (
      <div
        className="flex items-center justify-center h-full"
        dangerouslySetInnerHTML={{ __html: customLogos[brand.custom_logo] }}
        style={{ maxHeight: '52px', maxWidth: '140px' }}
      />
    );
  }

  const iconKey = brand.slug ? `si${brand.slug.charAt(0).toUpperCase()}${brand.slug.slice(1)}` : null;
  const icon = iconKey ? simpleIcons[iconKey] : null;

  if (icon && icon.svg) {
    const color = `#${icon.hex}`;
    const coloredSvg = icon.svg.replace('<svg ', `<svg fill="${color}" `);
    return (
      <div
        className="flex items-center justify-center"
        dangerouslySetInnerHTML={{ __html: coloredSvg }}
        style={{ width: '52px', height: '52px' }}
      />
    );
  }

  if (brand.text_logo_text) {
    return (
      <div className="flex flex-col items-center justify-center leading-tight">
        <div
          className="text-2xl font-extrabold tracking-tight"
          style={{ color: brand.text_logo_color || '#000' }}
        >
          {brand.text_logo_text}
        </div>
        {brand.text_logo_subtitle && (
          <div
            className="text-sm font-semibold tracking-wider"
            style={{ color: brand.text_logo_color || '#000', opacity: 0.7 }}
          >
            {brand.text_logo_subtitle}
          </div>
        )}
      </div>
    );
  }

  return <div className="text-base font-bold text-gray-800">{brand.name}</div>;
};

export const Brands = () => {
  const { settings } = useSettings();
  const s = settings || {};
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGetBrands()
      .then(({ data }) => setBrands(data))
      .catch((e) => console.error('Failed to load brands:', e))
      .finally(() => setLoading(false));
  }, []);

  const duplicatedBrands = [...brands, ...brands];

  return (
    <section id="brands" className="py-20 md:py-28 bg-gradient-to-b from-rose-100 via-pink-100 to-rose-200 relative overflow-hidden">
      {/* Bottom blend to dark contact */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-gray-800 pointer-events-none z-[1]"></div>
      {/* Decorative orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-rose-200 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
      <div className="absolute top-1/2 left-0 w-64 h-64 bg-amber-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-white border-2 border-orange-200 px-5 py-2.5 rounded-full shadow-md mb-6">
            <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
            <span className="text-base font-bold tracking-wider text-orange-700 uppercase">
              {s.brands_eyebrow || 'Trusted Partners'}
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 leading-[1.05]">
            {s.brands_headline_line1 || 'Brands that trust us with their'}{' '}
            <span className="italic font-serif text-orange-600">{s.brands_headline_italic || 'stories'}</span>
          </h2>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-orange-600" size={32} />
        </div>
      ) : (
        <div className="relative w-full overflow-hidden py-4">
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-rose-100 to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-rose-200 to-transparent z-10 pointer-events-none"></div>

          {/* Packed marquee — back-to-back, minimal gaps */}
          <div className="flex marquee-slide items-center">
            {duplicatedBrands.map((brand, index) => (
              <div
                key={`brand-${index}`}
                className="flex-shrink-0 flex items-center justify-center h-16 md:h-20 px-3 md:px-4 hover:scale-110 transition-transform duration-300"
                data-testid={`brand-logo-${brand.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <BrandLogo brand={brand} />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
